import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { useNavigate, useParams } from 'react-router-dom'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import Dots from '../../icons/Dots'
import ConfirmButtonModal from '../../components/ConfirmButtonModal'
import Radom, { Customer, Deposit, DepositAccount, DepositAccountType, ISubscription } from '../../state/Radom'
import Spinner from '../../components/Spinner'
import { Container } from '../../components/Animations'
import BreadCrumbs from '../../components/Breadcrumbs'
import User from '../../state/User'
import styled from 'styled-components'
import { RADOM_COLORS } from '../../util/Constants'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import Edit from '../../icons/Edit'
import EditTagsModal from './EditTags'
import { errorToast, infoToast, successToast, useTestMode } from '../../util/Util'
import { prodNetworks, testNetworks } from '../../util/Managed'
import { StatusBadge } from '../CheckoutSessions'
import Rotate from '../../icons/Rotate'
import { genSectionItems, MDASH } from '../../util/SectionItems'
import { Section, SubTitle, SectionContent, SectionItem } from '../../components/Shared'
import { CustomerTabs } from './CustomerTypes'
import CustomerTransactions from './CustomerTransactions'
import AnimatedTabs from '../../components/AnimatedTabs'
import CustomerDepositAccounts from './CustomerDepositAccounts'
import CustomerPayoutsAccounts from './CustomerPayoutsAccounts'
import CreateExternalAccountModal from './CreateExternalAccountModal'
import CopyButton from '../../components/CopyButton'
import { usePermissions } from '../../util/permissions'
import Gate from '../../components/Gate'
import { Surface } from '../../components/DashboardPrimitives'
import RecipientAvatar from '../Send/RecipientAvatar'

// ---------------------------------------------------------------------------------------------------------------------
export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  align-items: center;
`

export const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  background-color: ${RADOM_COLORS.GRAY9};
  border-radius: 5px;
  padding: 5px 10px;
`

// ---------------------------------------------------------------------------------------------------------------------
const ProfileCard = styled.div`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--surface-pressed, #edf1f7);
  border-radius: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 18px 20px;
`

const ProfileText = styled.div`
  display: flex;
  flex: 1 1 240px;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`

const ProfileName = styled.div`
  color: var(--ink-strong, #121722);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ProfileMeta = styled.div`
  align-items: center;
  color: var(--text-muted, #6b7280);
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 10px;
`

const SectionLabel = styled.div`
  color: var(--text-muted, #6b7280);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`

const ProfileActions = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-left: auto;
`

const CustomerView = observer((): ReactElement => {
  const params = useParams()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(true)
  const [customer, setCustomer] = useState<Customer>()
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([])
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [depositAccounts, setDepositAccounts] = useState<DepositAccount[]>([])
  const [activeTab, setActiveTab] = useState(CustomerTabs.Transactions)
  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false)
  const [isCreatePayoutAccountModalOpen, setIsCreatePayoutAccountModalOpen] = useState(false)
  const payoutAccountsRefreshRef = useRef<(() => void) | null>(null)
  const [testMode] = useTestMode()
  const [idCardRotationAngle, setIdCardRotationAngle] = useState(0)
  const [selfieRotationAngle, setSelfieRotationAngle] = useState(0)
  const [isLoadingDepositAccounts, setIsLoadingDepositAccounts] = useState(false)
  const { has } = usePermissions()

  const loadCustomer = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getCustomer(params.id, testMode ? testNetworks : prodNetworks)
      .then(s => setCustomer(s))
      .catch(e => console.error('Failed to look up checkout session', e))
      .finally(() => setIsLoading(false))
  }

  const fetchSubscriptions = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getCustomerSubscriptions(params.id)
      .then(subs => {
        setSubscriptions(subs)
      })
      .catch(e => console.error('Failed to get this customer\'s subscriptions', e))
      .finally(() => setIsLoading(false))
  }

  const fetchDeposits = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getCustomerDeposits(params.id)
      .then(deposits => {
        setDeposits(deposits)
      })
      .catch(e => console.error('Failed to get this customer\'s deposits', e))
      .finally(() => setIsLoading(false))
  }

  const fetchDepositAccounts = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoadingDepositAccounts(true)
    Radom.listDepositAccounts(testMode, 0, 250, true, [DepositAccountType.Crypto])
      .then(res => {
        setDepositAccounts(
          res.data.filter(account =>
            account.customer?.id === params.id ||
            account.accountDetails.Crypto?.customerId === params.id
          )
        )
      })
      .catch(e => {
        console.error('Failed to get this customer\'s deposit accounts', e)
        errorToast('Failed to load contact deposit addresses')
      })
      .finally(() => setIsLoadingDepositAccounts(false))
  }

  useEffect(() => { loadCustomer() }, [testMode])

  const canReadSubscriptions = has('ReadSubscriptions')
  const canReadDeposits = has('ReadDeposit')
  const canReadDepositAccounts = has('ReadDepositAccount')

  useEffect(() => {
    if (canReadSubscriptions) fetchSubscriptions()
    if (canReadDeposits) fetchDeposits()
    if (canReadDepositAccounts) fetchDepositAccounts()
  }, [customer, canReadSubscriptions, canReadDeposits, canReadDepositAccounts])
  const customerColumnCount = 2
  const subscriptionColumnCount = 3
  const depositColumnCount = 3

  const visibleTabs = Object.values(CustomerTabs).filter(tab => {
    if (tab === CustomerTabs.DepositAccounts) return has('ReadDepositAccount')
    // Ways to pay the contact sit on the page itself, above the tabs.
    if (tab === CustomerTabs.PayoutAccounts) return false
    return true
  })
  const effectiveTab = visibleTabs.includes(activeTab) ? activeTab : (visibleTabs[0] ?? CustomerTabs.Transactions)

  const getActiveTabContent = (): JSX.Element => {
    switch (effectiveTab) {
    case CustomerTabs.Transactions:
      return <CustomerTransactions
        customer={customer}
        subscriptions={subscriptions}
        subscriptionColumnCount={subscriptionColumnCount}
        deposits={deposits}
        depositColumnCount={depositColumnCount}
      />
    case CustomerTabs.DepositAccounts:
      return <CustomerDepositAccounts
        depositAccounts={depositAccounts}
        isLoading={isLoadingDepositAccounts}
        fetchDepositAccounts={fetchDepositAccounts}
        customerId={customer?.id ?? '' }
      />
    case CustomerTabs.PayoutAccounts:
      return <CustomerPayoutsAccounts
        customerId={customer?.id ?? ''}
        isLoading={isLoading}
        onRefreshRef={payoutAccountsRefreshRef}
        onCreateAccount={() => setIsCreatePayoutAccountModalOpen(true)}
      />
    default:
      return <></>
    }
  }

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (!customer) {
    return <></>
  }
  const isBusiness = customer.accountType === 'Business'

  // -------------------------------------------------------------------------------------------------------------------
  return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

      <BreadCrumbs items={[
        { item: 'Contacts', to: '/customers' },
        { item: customer.name || customer.email || customer.id, to: '.' }
      ]} />

    </div>

    <ProfileCard>
      <RecipientAvatar name={customer.name || customer.email || 'Contact'} size={56} />
      <ProfileText>
        <ProfileName>{customer.name || customer.email}</ProfileName>
        <ProfileMeta>
          {customer.email && <span>{customer.email}</span>}
          <span>{isBusiness ? 'Business' : 'Individual'}</span>
          {customer.tags.includes('identity_verified') &&
            <StatusBadge status='active'>KYC Verified</StatusBadge>}
        </ProfileMeta>
      </ProfileText>
      <ProfileActions>
        <Gate permission='WriteExternalAccount'>
          <SecondaryButton onClick={() => setIsCreatePayoutAccountModalOpen(true)}>Add account</SecondaryButton>
        </Gate>
        <Gate permission='WritePayouts'>
          <PrimaryButton
            style={{ minWidth: 150 }}
            onClick={() => navigate(`/send?customer=${customer.id}`)}>
            Send funds
          </PrimaryButton>
        </Gate>
        <Dropdown
          noChevron
          iconTrigger
          selectedContent={<Dots width={15} />}
          dropdownContent={
            <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
              <DropdownItem onClick={() => {
                navigator.clipboard.writeText(customer.id)
                infoToast('Contact ID copied to clipboard')
              }}>Copy contact ID</DropdownItem>
              <Gate permission='WriteCustomers'>
                <ConfirmButtonModal
                  title='Delete contact'
                  description='Are you sure you want to delete this contact? This action cannot be undone.'
                  action='Delete contact'
                  onConfirm={async () => {
                    try {
                      await Radom.deleteCustomer(customer.id)
                      successToast('Contact deleted')
                      navigate('/customers')
                    } catch (err: any) {
                      errorToast(err.response?.data?.error || err.message)
                    }
                  }}
                  ActionComponent={(props: any) => <DropdownItem {...props}>Delete contact</DropdownItem>}
                />
              </Gate>
            </div>
          }
        />
      </ProfileActions>
    </ProfileCard>

    <Gate permission='ReadExternalAccount'>
      <Surface>
        <SectionLabel>Ways to pay</SectionLabel>
        <CustomerPayoutsAccounts
          customerId={customer.id}
          isLoading={false}
          onRefreshRef={payoutAccountsRefreshRef}
          onCreateAccount={() => setIsCreatePayoutAccountModalOpen(true)}
          hideToolbar
        />
      </Surface>
    </Gate>

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Customer details
      // ---------------------------------------------------------------------------------------------------------------
    }
    <Section>
      <SectionLabel>Details</SectionLabel>

      <SectionContent numColumns={customerColumnCount}>
        {
          isBusiness &&
          genSectionItems([
            { heading: 'Company Name', content: customer.companyName },
            { heading: 'Company Number', content: customer.companyNumber },
            { heading: '', content: null }
          ], customerColumnCount)
        }

        {
          genSectionItems([
            { heading: 'Name', content: customer.name },
            { heading: 'Email Address', content: customer.email },
            { heading: 'Address', content: customer.billingAddress },
            { heading: 'Date of Birth', content: customer.dob },
            { heading: 'Country of Residence', content: customer.residenceCountry }
          ], customerColumnCount)
        }

        {
          User.organization &&
          <SectionItem>
            <SubTitle>Total spend</SubTitle>
            <div>
              {
                Number(customer.totalSpend) <= 0
                  ? MDASH
                  : new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: User.organization?.defaultCurrency
                  }).format(Number(customer.totalSpend))
              }
            </div>
          </SectionItem>
        }

        <SectionItem>
          <SubTitle>
            <span>Tags</span>
            <Gate permission='WriteCustomers'>
              <SecondaryButton
                onClick={() => setIsTagsModalOpen(true)}
                style={{ padding: 0, border: 0, fontSize: 0, minHeight: 0, height: 16, lineHeight: 0 }}>
                <Edit stroke={RADOM_COLORS.GRAY_DARKER} style={{ height: 15 }} />
              </SecondaryButton>
            </Gate>
          </SubTitle>
          <TagsContainer>
            {customer.tags.length <= 0 && MDASH}
            {customer.tags.map((t, i) => <Tag key={i}>{t}</Tag>)}
          </TagsContainer>

        </SectionItem>

        {
          genSectionItems([
            { heading: 'Phone', content: customer.phone },
            { heading: 'Telegram', content: customer.telegram },
            { heading: 'Discord', content: customer.discord }
          ], customerColumnCount)
        }

        <SectionItem>
          <SubTitle>Contact ID</SubTitle>
          <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 10, alignItems: 'center' }}>
            <span style={{ color: 'var(--text-muted, #6b7280)' }}>{customer.id}</span>
            <CopyButton text={customer.id} />
          </div>
        </SectionItem>
      </SectionContent>
    </Section>

    <AnimatedTabs
      selectedTab={effectiveTab}
      tabs={visibleTabs}
      onTabChange={(tab) => setActiveTab(tab as CustomerTabs)}
    />

    <CreateExternalAccountModal
      presentation='drawer'
      customerId={customer?.id ?? ''}
      contact={customer}
      title={`Add a way to pay ${customer?.name || customer?.email || 'this contact'}`}
      visible={isCreatePayoutAccountModalOpen}
      onClose={() => setIsCreatePayoutAccountModalOpen(false)}
      onAccountCreated={() => {
        setIsCreatePayoutAccountModalOpen(false)
        payoutAccountsRefreshRef.current?.()
      }}
    />

    {getActiveTabContent()}
    {
      // ---------------------------------------------------------------------------------------------------------------
      // Customer KYC info
      // ---------------------------------------------------------------------------------------------------------------
      customer.tags.length > 0 &&
      customer.tags.includes('identity_verified') &&
      <Section>
        <SubTitle style={{ fontSize: 18 }}>
          Verifications
        </SubTitle>
        <SectionContent
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between', // 添加这一行
            gap: 20,
            width: '100%' // 建议加上宽度控制
          }}
        >

          <SectionItem style={{ flex: 1 }}>
            <SubTitle style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}>Passport/IDcard
              <Rotate
                width={15}
                height={15}
                onClick={() => {
                  setIdCardRotationAngle(idCardRotationAngle + 90)
                }}
              />
            </SubTitle>
            <div style={{ padding: 10, overflow: 'hidden' }}>
              {customer.kycData?.idCardUrl || customer.kycData?.passportUrl
                ? <img
                  src={customer.kycData?.idCardUrl || customer.kycData?.passportUrl}
                  onClick={() =>
                    window.open(customer.kycData?.idCardUrl || customer.kycData?.passportUrl, '_blank')
                  }
                  style={{
                    transform: `rotate(${idCardRotationAngle}deg)`,
                    maxHeight: 300,
                    height: 'auto',
                    width: '100%',
                    objectFit: 'contain',
                    borderRadius: 10
                  }}
                />
                : null}
            </div>
          </SectionItem>
          <SectionItem style={{ flex: 1 }}>
            <SubTitle style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}>Selfie
              <Rotate
                width={15}
                height={15}
                onClick={() => {
                  setSelfieRotationAngle(selfieRotationAngle + 90)
                }}
              />
            </SubTitle>
            <div
              style={{
                width: 'auto',
                maxHeight: 300,
                overflow: 'hidden',
                padding: 10
              }}
            >
              <img
                src={customer.kycData?.selfieUrl}
                onClick={() => window.open(customer.kycData?.selfieUrl, '_blank')}
                style={{
                  transform: `rotate(${selfieRotationAngle}deg)`,
                  maxHeight: 300,
                  height: 'auto',
                  width: '100%',
                  objectFit: 'contain',
                  borderRadius: 10
                }}
              />
            </div>
          </SectionItem>
        </SectionContent>
      </Section>
    }

    {
      isTagsModalOpen &&
      <EditTagsModal
        onClose={() => setIsTagsModalOpen(false)}
        isOpen={isTagsModalOpen}
        tags={customer.tags}
        onSave={async tags => {
          try {
            await Radom.updateCustomer(customer.id, { tags })
            setIsTagsModalOpen(false)
            loadCustomer()
          } catch (err) {
            errorToast(err.response?.data?.error || err.message)
          }
        }} />
    }
  </Container>
})

export default CustomerView
